import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

const NetworkGraph = () => {
  const svgRef = useRef(null);

  useEffect(() => {
    if (!svgRef.current) return;

    // Clear any existing SVG content
    d3.select(svgRef.current).selectAll("*").remove();

    // Sample data with job matches and roles
    const data = {
      nodes: [
        { 
          id: "you", 
          type: "person", 
          name: "You", 
          role: "Software Engineer",
          avatar: "https://ui-avatars.com/api/?name=You&length=3&background=3b82f6&color=fff"
        },
        { 
          id: "connection1", 
          type: "connection", 
          name: "Sarah Chen", 
          role: "Engineering Manager at Google",
          avatar: "https://ui-avatars.com/api/?name=Sarah+Chen&background=fff&color=3b82f6"
        },
        { 
          id: "connection2", 
          type: "connection", 
          name: "Mike Johnson", 
          role: "Senior Engineer at Meta",
          avatar: "https://ui-avatars.com/api/?name=Mike+Johnson&background=fff&color=3b82f6"
        },
        { 
          id: "connection3", 
          type: "connection", 
          name: "Lisa Wong", 
          role: "Tech Lead at Amazon",
          avatar: "https://ui-avatars.com/api/?name=Lisa+Wong&background=fff&color=3b82f6"
        },
        { 
          id: "company1", 
          type: "company", 
          name: "Google", 
          matches: 1,
          position: "Software Developer L4",
          salary: "$150K - $200K",
          logo: "https://www.google.com/images/branding/googlelogo/1x/googlelogo_color_272x92dp.png",
          location: "Mountain View, CA"
        },
        { 
          id: "company2", 
          type: "company", 
          name: "Meta", 
          matches: 1,
          position: "Software Developer E4",
          salary: "$160K - $210K",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/512px-Meta_Platforms_Inc._logo.svg.png",
          location: "Menlo Park, CA"
        },
        { 
          id: "company3", 
          type: "company", 
          name: "Amazon", 
          matches: 1,
          position: "Software Developer L5",
          salary: "$155K - $205K",
          logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a9/Amazon_logo.svg/1024px-Amazon_logo.svg.png",
          location: "Seattle, WA"
        },
      ],
      links: [
        { source: "you", target: "connection1", strength: 0.8 },
        { source: "you", target: "connection2", strength: 0.7 },
        { source: "you", target: "connection3", strength: 0.9 },
        { source: "connection1", target: "company1", strength: 1 },
        { source: "connection2", target: "company2", strength: 1 },
        { source: "connection3", target: "company3", strength: 1 },
      ]
    };

    // Set up SVG dimensions with margin
    const margin = { top: 20, right: 20, bottom: 20, left: 20 };
    const width = 600 - margin.left - margin.right;
    const height = 400 - margin.top - margin.bottom;

    // Create SVG container with gradient background
    const svg = d3.select(svgRef.current)
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .attr("viewBox", [-width / 2, -height / 2, width, height])
      .style("background", "radial-gradient(circle at center, #f0f9ff 0%, #ffffff 100%)");

    // Define gradients and filters
    const defs = svg.append("defs");

    // Link gradient
    const linkGradient = defs.append("linearGradient")
      .attr("id", "link-gradient")
      .attr("gradientUnits", "userSpaceOnUse");

    linkGradient.append("stop")
      .attr("offset", "0%")
      .attr("stop-color", "#3b82f6")
      .attr("stop-opacity", 0.8);

    linkGradient.append("stop")
      .attr("offset", "100%")
      .attr("stop-color", "#93c5fd")
      .attr("stop-opacity", 0.4);

    // Glow filter
    const filter = defs.append("filter")
      .attr("id", "glow")
      .attr("x", "-50%")
      .attr("y", "-50%")
      .attr("width", "200%")
      .attr("height", "200%");

    filter.append("feGaussianBlur")
      .attr("stdDeviation", "3")
      .attr("result", "coloredBlur");

    const feMerge = filter.append("feMerge");
    feMerge.append("feMergeNode").attr("in", "coloredBlur");
    feMerge.append("feMergeNode").attr("in", "SourceGraphic");

    // Add patterns for avatars and logos
    data.nodes.forEach(node => {
      const imageUrl = node.type === 'company' ? node.logo : node.avatar;
      const patternId = `pattern-${node.id}`;
      
      defs.append("pattern")
        .attr("id", patternId)
        .attr("width", 1)
        .attr("height", 1)
        .attr("patternUnits", "objectBoundingBox")
        .append("image")
        .attr("xlink:href", imageUrl)
        .attr("width", node.type === 'company' ? 60 : 40)
        .attr("height", node.type === 'company' ? 60 : 40)
        .attr("x", node.type === 'company' ? 0 : 10)
        .attr("y", node.type === 'company' ? 0 : 10);
    });

    // Helper functions
    function getNodeRadius(d) {
      switch (d.type) {
        case "person": return 35;
        case "connection": return 25;
        case "company": return 30;
        default: return 20;
      }
    }

    function getNodeColor(d) {
      return `url(#pattern-${d.id})`;
    }

    // Create force simulation first
    const simulation = d3.forceSimulation(data.nodes)
      .force("link", d3.forceLink(data.links)
        .id(d => d.id)
        .distance(d => 100 + d.strength * 50))
      .force("charge", d3.forceManyBody().strength(d => 
        d.type === "person" ? -1000 : 
        d.type === "connection" ? -400 : -200
      ))
      .force("center", d3.forceCenter())
      .force("collision", d3.forceCollide().radius(d => getNodeRadius(d) + 20))
      .force("x", d3.forceX().strength(0.1))
      .force("y", d3.forceY().strength(0.1));

    // Create links with varying thickness based on strength
    const link = svg.append("g")
      .attr("class", "links")
      .selectAll("line")
      .data(data.links)
      .join("line")
      .attr("stroke", "url(#link-gradient)")
      .attr("stroke-width", d => d.strength * 3)
      .attr("stroke-opacity", 0.6);

    // Create nodes group
    const nodesGroup = svg.append("g").attr("class", "nodes");

    // Create nodes with enhanced styling
    const node = nodesGroup.selectAll("g")
      .data(data.nodes)
      .join("g")
      .attr("class", d => `node ${d.type}`)
      .call(drag(simulation));

    // Add circles to nodes with glow effect
    node.append("circle")
      .attr("r", d => getNodeRadius(d))
      .attr("fill", d => getNodeColor(d))
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2)
      .style("filter", d => d.type === "person" ? "url(#glow)" : "none");

    // Add labels with multiple lines
    const labels = node.append("g")
      .attr("class", "label");

    labels.append("text")
      .text(d => d.name)
      .attr("text-anchor", "middle")
      .attr("dy", d => getNodeRadius(d) + 20)
      .attr("fill", "#4b5563")
      .attr("font-size", "12px")
      .attr("font-weight", "600");

    labels.append("text")
      .text(d => {
        if (d.type === "company") return d.position;
        return d.role || "";
      })
      .attr("text-anchor", "middle")
      .attr("dy", d => getNodeRadius(d) + 35)
      .attr("fill", "#6b7280")
      .attr("font-size", "10px");

    // Add match badges to company nodes with animation
    const badges = node.filter(d => d.type === "company")
      .append("g")
      .attr("class", "match-badge")
      .attr("transform", "translate(20, -20)");

    badges.append("circle")
      .attr("r", 14)
      .attr("fill", "#10b981")
      .attr("stroke", "#ffffff")
      .attr("stroke-width", 2)
      .style("filter", "url(#glow)")
      .call(pulseAnimation);

    badges.append("text")
      .text("1")
      .attr("text-anchor", "middle")
      .attr("dy", "0.3em")
      .attr("fill", "#ffffff")
      .attr("font-size", "10px")
      .attr("font-weight", "bold");

    // Add hover interactions
    node.on("mouseover", handleMouseOver)
        .on("mouseout", handleMouseOut);

    function handleMouseOver(event, d) {
      // Find connected nodes
      const connectedNodes = new Set();
      const connectedLinks = new Set();
      
      data.links.forEach(link => {
        if (link.source.id === d.id) {
          connectedNodes.add(link.target.id);
          connectedLinks.add(link);
        }
        if (link.target.id === d.id) {
          connectedNodes.add(link.source.id);
          connectedLinks.add(link);
        }
      });

      // Highlight connected nodes and links
      // node.style("opacity", n => 
      //   n.id === d.id || connectedNodes.has(n.id) ? 1 : 0.2
      // );

      link.style("opacity", l => 
        connectedLinks.has(l) ? 0.8 : 0.1
      )
      .style("stroke-width", l => 
        connectedLinks.has(l) ? l.strength * 4 : l.strength * 2
      );

      // Show tooltip
      const tooltip = d3.select(".network-graph-container")
        .append("div")
        .attr("class", "tooltip")
        .style("position", "absolute")
        .style("left", `${event.pageX + 10}px`)
        .style("top", `${event.pageY - 10}px`);

      if (d.type === "company") {
        tooltip.html(`
          <strong>${d.name}</strong><br/>
          <span class="position">${d.position}</span><br/>
          <span class="salary">${d.salary}</span><br/>
          <span class="location">${d.location}</span>
        `);
      } else if (d.type === "connection") {
        tooltip.html(`
          <strong>${d.name}</strong><br/>
          ${d.role}
        `);
      }
    }

    function handleMouseOut() {
      // Reset all elements to original state
      node.style("opacity", 1);
      link.style("opacity", 0.6)
          .style("stroke-width", d => d.strength * 3);
      d3.selectAll(".tooltip").remove();
    }

    function pulseAnimation(selection) {
      function repeat() {
        selection
          .transition()
          .duration(1000)
          .attr("r", 16)
          .style("filter", "url(#glow)")
          .transition()
          .duration(1000)
          .attr("r", 14)
          .style("filter", "none")
          .on("end", repeat);
      }
      repeat();
    }

    // Drag functionality
    function drag(simulation) {
      function dragstarted(event) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }

      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }

      function dragended(event) {
        if (!event.active) simulation.alphaTarget(0);
        event.subject.fx = null;
        event.subject.fy = null;
      }

      return d3.drag()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    }

    // Update positions on each tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => d.source.x)
        .attr("y1", d => d.source.y)
        .attr("x2", d => d.target.x)
        .attr("y2", d => d.target.y);

      node
        .attr("transform", d => `translate(${d.x},${d.y})`);
    });

    // Cleanup
    return () => {
      simulation.stop();
    };
  }, []);

  return (
    <div className="network-graph-container">
      <svg ref={svgRef}></svg>
    </div>
  );
};

export default NetworkGraph; 